import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useUser } from '../../context/UserContext';
import { reloadForNewScope } from '../../utils/reloadForNewScope';

/**
 * OrgPicker - Dropdown to switch active organization (DOS-27).
 *
 * Lists the user's own orgs, then the sample dealers' orgs any signed-in user
 * may enter (skills plan step 4a) — synthetic data for demos and testing.
 * Hidden when there is nothing to switch to.
 *
 * The menu opens upward in a portal with fixed positioning, sized to the
 * space above the button and scrollable, as LocationSwitcher's does. Drawn in
 * place, the sidebar's scrolling Settings & Admin panel cut off its top.
 */
function OrgPicker() {
  const { org, orgs, sampleOrgs = [], switchOrg } = useUser();
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const ref = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const [menuStyle, setMenuStyle] = useState(null);

  // Close on outside click (the menu lives in a portal, so check it too)
  useEffect(() => {
    function handleClick(e) {
      const inside = ref.current?.contains(e.target) || menuRef.current?.contains(e.target);
      if (!inside) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Place the menu just above the button, in the space above it.
  useLayoutEffect(() => {
    if (!open || !buttonRef.current) return undefined;
    function place() {
      const r = buttonRef.current.getBoundingClientRect();
      const gap = 4;
      setMenuStyle({
        position: 'fixed',
        left: r.left,
        width: r.width,
        bottom: window.innerHeight - r.top + gap,
        maxHeight: Math.max(160, r.top - 2 * gap),
      });
    }
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  const own = orgs || [];
  const ownIds = new Set(own.map(o => o.id));
  // A sample org the user is already a member of stays under their own orgs.
  const samples = sampleOrgs.filter(o => !ownIds.has(o.id));
  if (own.length + samples.length <= 1) return null;

  const handleSwitch = async (orgId) => {
    if (orgId === org?.id) { setOpen(false); return; }
    try {
      setSwitching(true);
      await switchOrg(orgId);
      setOpen(false);
      reloadForNewScope();
    } catch (err) {
      console.error('Org switch failed:', err);
    } finally {
      setSwitching(false);
    }
  };

  return (
    <div ref={ref} className="relative">
      <button
        ref={buttonRef}
        data-testid="org-picker-button"
        onClick={() => setOpen(!open)}
        disabled={switching}
        className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors
          text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
      >
        {/* Building icon */}
        <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
        <span className="flex-1 text-left truncate">{org?.name || 'Select org'}</span>
        {org?.sample && <span className="shrink-0 text-xs text-gray-500 dark:text-gray-400">Sample</span>}
        <svg className={`w-3 h-3 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && createPortal(
        <div
          ref={menuRef}
          data-testid="org-picker-menu"
          style={menuStyle || { position: 'fixed', visibility: 'hidden' }}
          className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700
            rounded-lg shadow-lg z-50 overflow-y-auto">
          {own.map(o => (
            <OrgRow key={o.id} o={o} active={o.id === org?.id} onClick={() => handleSwitch(o.id)} />
          ))}
          {samples.length > 0 && (
            <>
              <div className="px-3 pt-2 pb-1 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700">
                Sample dealers
              </div>
              {samples.map(o => (
                <OrgRow key={o.id} o={o} active={o.id === org?.id} onClick={() => handleSwitch(o.id)} />
              ))}
            </>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}

function OrgRow({ o, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors
        ${active
          ? 'bg-decisiv-blue/10 text-decisiv-blue font-medium'
          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
        }`}
    >
      <span className="flex-1 truncate">{o.name}</span>
      {active && (
        <svg className="w-3.5 h-3.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
      )}
    </button>
  );
}

export default OrgPicker;
